import React, { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/App";
import axios from "axios";
import { 
  Building2,
  TrendingUp,
  RefreshCw,
  PieChart,
  BarChart3,
  IndianRupee
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend } from "recharts";

const COLORS = ['#F59E0B', '#3B82F6', '#22C55E', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'];

export const AMCAUMPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/funds/aum/top-amcs`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching AUM data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatAUM = (value) => {
    if (value >= 100000) return `₹${(value/100000).toFixed(1)}L Cr`;
    return `₹${value.toLocaleString('en-IN')} Cr`;
  };

  const chartData = data?.amcs?.map((amc, idx) => ({
    name: amc.name.replace(' Mutual Fund', '').replace(' MF', ''),
    aum: amc.aum,
    share: amc.market_share,
    schemes: amc.schemes,
    color: COLORS[idx % COLORS.length]
  })) || [];

  const pieData = chartData.slice(0, 6).map(d => ({
    name: d.name,
    value: d.share,
    color: d.color
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="amc-aum-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Building2 className="w-8 h-8 text-primary" />
            AMC Assets Under Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Top Asset Management Companies by AUM • Data as of {data?.data_as_of}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Top 10 AMCs AUM</div>
                  <div className="text-2xl font-bold text-primary">
                    {formatAUM(data?.total_top_10_aum || 0)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Market Leader</div>
                  <div className="text-xl font-bold">{data?.amcs?.[0]?.name?.replace(' Mutual Fund', '')}</div>
                  <div className="text-sm text-green-500">{data?.amcs?.[0]?.market_share}% market share</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Total Schemes</div>
                  <div className="text-2xl font-bold">
                    {data?.amcs?.reduce((sum, amc) => sum + amc.schemes, 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar Chart */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                AUM by AMC (in Crores)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis type="number" stroke="#9CA3AF" tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K Cr`} />
                    <YAxis type="category" dataKey="name" stroke="#9CA3AF" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #374151' }}
                      formatter={(value) => [`₹${value.toLocaleString('en-IN')} Cr`, 'AUM']}
                    />
                    <Bar dataKey="aum" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Pie Chart */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-primary" />
                Market Share Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${value}%`}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #374151' }}
                      formatter={(value) => [`${value}%`, 'Market Share']}
                    />
                    <Legend 
                      layout="horizontal" 
                      verticalAlign="bottom"
                      formatter={(value) => <span className="text-xs">{value}</span>}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AMC Table */}
        <Card className="bg-card/50 border-border/50">
          <CardHeader>
            <CardTitle>Top 10 AMCs - Detailed View</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Rank</th>
                    <th className="text-left p-4 font-medium">AMC Name</th>
                    <th className="text-right p-4 font-medium">AUM (Cr)</th>
                    <th className="text-right p-4 font-medium">Market Share</th>
                    <th className="text-right p-4 font-medium">No. of Schemes</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.amcs?.map((amc, index) => (
                    <tr key={amc.name} className="border-t border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                          style={{ backgroundColor: COLORS[index % COLORS.length] + '30', color: COLORS[index % COLORS.length] }}
                        >
                          {index + 1}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{amc.name}</div>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-bold text-primary">₹{amc.aum.toLocaleString('en-IN')}</span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full"
                              style={{ 
                                width: `${amc.market_share * 6}%`, 
                                backgroundColor: COLORS[index % COLORS.length] 
                              }}
                            />
                          </div>
                          <span className="text-sm">{amc.market_share}%</span>
                        </div>
                      </td>
                      <td className="p-4 text-right">{amc.schemes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Note */}
        <Card className="bg-blue-500/10 border-blue-500/20">
          <CardContent className="p-4 text-sm text-blue-200">
            <strong>Note:</strong> {data?.note} This data represents AUM for the mutual fund industry's top players 
            and is typically updated monthly based on AMFI reports.
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
